import React, { useMemo, useState, useEffect } from 'react';
import { get } from 'lodash';
import { useSelector, useDispatch } from 'react-redux';
import { ForgeDivider, ForgeSkeleton } from '@tylertech/forge-react';
import { ISelectOption } from '@tylertech/forge';
import { I18n } from 'common/i18n';
import { TableActionsSelect } from './AgGridUpdated/AgGridComponents/TableActionsSelect';
import { ActionSelectorCheckbox } from './ActionSelectorCheckbox';
import { MultiActionSelector } from './AgGridUpdated/AgGridComponents/MultiActionSelector';

import { useTableActionsResults } from 'common/visualizations/views/agGridReact/customHooks/useTableActionsResults';
import {
  setShowExistingActionColumn,
  setShowTableActions,
  setSelectedExistingColumn,
  setSelectedActionForExistingColumn,
  setNewTableActions,
  setTableActionsContextFieldsMapping,
  setTableActionsMediaType,
} from 'common/authoring_workflow/actions.js';
import {
  getActionsMediaType,
  getConfiguration,
  getShowTableActions,
  getShowExistingActionColumn,
  getSelectedExistingColumn,
  getNewTableActions,
  getSelectedActionForExistingColumn,
  getTableColumns,
  getContextFieldMappings,
} from 'common/authoring_workflow/selectors/vifAuthoring.js';
import { AuthoringWorkflowState } from 'common/authoring_workflow/reducers/types';
import { getCurrentMetadata, getDisplayableColumns } from 'common/authoring_workflow/selectors/metadata';
import {
  getTableActionsContext,
  hasEmbeddedExecutionMethod,
  ProcessDescriptor,
} from 'common/visualizations/views/agGridReact/helpers/tableActionsHelper';
import { TableActionsContextField } from 'common/visualizations/views/agGridReact/TableActionsButton/types';
import { ViewColumn } from 'common/types/viewColumn';
import entityMappingsConfig from 'common/authoring_workflow/config/actionMediaTypeMappings.json';
import './TableActions.scss';

const scope = 'shared.visualizations.panes.presentation.fields.actions';

interface EntityConfig {
  mediaType: string;
  contextIdToColumnMap: Record<string, string>;
}

interface EntityMappings {
  [entityName: string]: EntityConfig[];
}

export const TableActions = () => {
  const dispatch = useDispatch();

  const vifAuthoring = useSelector((state: AuthoringWorkflowState) => state.vifAuthoring);
  const metadata = useSelector((state: AuthoringWorkflowState) =>
    getCurrentMetadata(state.metadataCollection, state.vifAuthoring),
  );
  const { data } = metadata;

  const getDropdownOptions = (columns: ViewColumn[]): ISelectOption[] => {
    return columns.map((column: ViewColumn) => {
      return {
        label: column.name || '',
        value: column.fieldName,
      };
    });
  };

  const tableColumns = getTableColumns(vifAuthoring);
  const actionsMediaType = getActionsMediaType(vifAuthoring);
  const displayableColumns = getDisplayableColumns(metadata);
  const tableColumnDroptionOptions = getDropdownOptions(tableColumns);
  const primaryKeyColumnDroptionOptions = getDropdownOptions(displayableColumns);
  const vifConfig = getConfiguration(vifAuthoring);
  const contextParams = getTableActionsContext(data, vifConfig);
  const showExistingActionColumn = getShowExistingActionColumn(vifAuthoring);
  const showTableActions = getShowTableActions(vifAuthoring);
  const selectedExistingColumn = getSelectedExistingColumn(vifAuthoring);
  const selectedActionForExistingColumn = getSelectedActionForExistingColumn(vifAuthoring);
  const newTableActions = getNewTableActions(vifAuthoring);
  const storedContextFieldMappings = getContextFieldMappings(vifAuthoring);
  const { tableActionsResults, loading } = useTableActionsResults(contextParams, {
    showAllActions: true,
  });

  const { processDescriptors = [] } = get(tableActionsResults, '[0]', {});

  const excludeEmbeddedProcessDescriptors = processDescriptors.filter(
    (action: ProcessDescriptor) => !hasEmbeddedExecutionMethod(action),
  );

  const actionOptions = useMemo(() => {
    return excludeEmbeddedProcessDescriptors.map((processDescriptor: { name: string; id: string }) => {
      return {
        label: processDescriptor.name,
        value: processDescriptor.id,
      };
    });
  }, [excludeEmbeddedProcessDescriptors]);

  // Helper functions
  const formatEntityName = (entityName: string): string | null => {
    // Convert "AccountsReceivable.Customers" to "Customers"
    if (!entityName) {
      return null;
    }
    const lastPart = entityName.split('.').pop();
    return lastPart?.replace(/([A-Z])/g, ' $1').trim() || null;
  };

  const formatContextName = (contextId: string): string | null => {
    // Convert "tyler.munis.accountsreceivable.customer.customerNumber" to "Customer Number"
    if (!contextId) {
      return null;
    }
    const lastPart = contextId.split('.').pop();
    return lastPart?.replace(/([A-Z])/g, ' $1').replace(/^./, (str) => str.toUpperCase()) || null;
  };

  const onEntityTypeSelect = (event: CustomEvent) => {
    const entityType = event.detail as string;
    setSelectedEntityType(entityType);

    // Get the context fields needed for this entity
    const entityConfigs = entityMappings[entityType];

    if (!entityConfigs || !Array.isArray(entityConfigs) || entityConfigs.length === 0) {
      console.warn(`No configuration found for entity type: ${entityType}`);
      setContextFields([]);
      return;
    }

    const entityConfig = entityConfigs[0]; // Using first config per 1-to-1 requirement

    if (!entityConfig.contextIdToColumnMap) {
      console.warn(`No context mapping found for entity: ${entityType}`);
      setContextFields([]);
      return;
    }

    const contextIdToColumnMap = entityConfig.contextIdToColumnMap;

    // Create field objects for the UI
    const fields = Object.keys(contextIdToColumnMap).map((contextId) => ({
      contextId: contextId,
      displayName: formatContextName(contextId) || contextId,
      selectedColumn: '',
      targetColumnName: contextIdToColumnMap[contextId],
    }));

    setContextFields(fields);

    // Persist context fields to Redux store
    dispatch(setTableActionsContextFieldsMapping(fields));

    // Also store the media type for this entity (auto-determined)
    dispatch(setTableActionsMediaType(entityConfig.mediaType));
  };

  const onContextFieldMapping = (fieldIndex: number, selectedColumn: string) => {
    if (fieldIndex < 0 || fieldIndex >= contextFields.length) {
      console.error(`Invalid field index: ${fieldIndex}, contextFields length: ${contextFields.length}`);
      return;
    }

    const updatedFields = [...contextFields];
    updatedFields[fieldIndex].selectedColumn = selectedColumn;
    setContextFields(updatedFields);
    dispatch(setTableActionsContextFieldsMapping(updatedFields));
  };

  const onColumnSelect = (event: CustomEvent) => {
    dispatch(setSelectedExistingColumn(event.detail));
  };

  const onActionSelect = (event: CustomEvent) => {
    dispatch(setSelectedActionForExistingColumn(event.detail));
  };

  const onNewActionClick = () => {
    dispatch(setShowTableActions(!showTableActions));
  };

  const onExistingActionClick = () => {
    dispatch(setShowExistingActionColumn(!showExistingActionColumn));
  };

  const onMultipleActionSelect = (actions: string[]) => {
    dispatch(setNewTableActions(actions));
  };

  const [entityMappings, setEntityMappings] = useState<EntityMappings>(
    entityMappingsConfig as EntityMappings,
  );
  const [mappingsLoading, setMappingsLoading] = useState(true);
  const [contextFields, setContextFields] = useState<TableActionsContextField[]>(
    Array.isArray(storedContextFieldMappings) ? storedContextFieldMappings : [],
  );

  const mediaTypeToKeyMap = useMemo(() => {
    return Object.entries(entityMappings).reduce<Record<string, string>>((acc, [topKey, arr]) => {
      if (Array.isArray(arr) && arr.length > 0) {
        const mediaType = arr[0].mediaType;
        if (mediaType) {
          acc[mediaType] = topKey;
        }
      }
      return acc;
    }, {});
  }, [entityMappings]);

  const [selectedEntityType, setSelectedEntityType] = useState(mediaTypeToKeyMap[actionsMediaType] || '');

  // Fetch entity mappings from API with fallback to static JSON
  useEffect(() => {
    const fetchEntityMappings = async () => {
      try {
        const response = await fetch('/api/v1/action_media_type_mappings');
        if (response.ok) {
          const apiMappings = await response.json();
          setEntityMappings(apiMappings);
        } else {
          // API call failed, use static JSON fallback
          setEntityMappings(entityMappingsConfig as EntityMappings);
        }
      } catch (error) {
        // Network error or other issue, use static JSON fallback
        console.warn('Failed to fetch entity mappings from API, using static fallback:', error);
        setEntityMappings(entityMappingsConfig as EntityMappings);
      } finally {
        setMappingsLoading(false);
      }
    };

    fetchEntityMappings();
  }, []);

  // Sync selectedEntityType when mediaTypeToKeyMap updates from async entity mappings load
  useEffect(() => {
    if (actionsMediaType && mediaTypeToKeyMap[actionsMediaType]) {
      const entityTypeFromMediaType = mediaTypeToKeyMap[actionsMediaType];
      if (entityTypeFromMediaType !== selectedEntityType) {
        setSelectedEntityType(entityTypeFromMediaType);
      }
    }
  }, [actionsMediaType, mediaTypeToKeyMap, selectedEntityType]);

  // Create entity dropdown options
  const entityTypeOptions = Object.keys(entityMappings || {}).map((entityName) => ({
    label: formatEntityName(entityName) || entityName,
    value: entityName,
  }));

  // Check if all context fields are mapped
  const allContextFieldsMapped =
    selectedEntityType && contextFields.map((field) => field.selectedColumn).every((str) => str !== '');

  return (
    <>
      <div className="authoring-field table-actions" key="actionColumnSelector">
        <div className="action-column-selector-container">
          {mappingsLoading ? (
            <ForgeSkeleton form-field></ForgeSkeleton>
          ) : (
            <div className="entity-type-section">
              <div className="forge-typography--subheading1">{I18n.t('linked_column', { scope })}</div>
              <TableActionsSelect
                options={entityTypeOptions}
                placeHolder={I18n.t('entity_type_placeholder', { scope })}
                value={selectedEntityType}
                onChange={onEntityTypeSelect}
                label={I18n.t('entity_type', { scope })}
              />
            </div>
          )}

          {selectedEntityType && contextFields.length > 0 && (
            <div className="context-mappings-section">
              <div className="forge-typography--subheading1">{I18n.t('field_mapping', { scope })}</div>
              <div className="context-mappings-description forge-typography--label1">
                {I18n.t('field_mapping_description', { scope })}
              </div>

              {contextFields.map((field, index) => (
                <div key={field.contextId} className="context-mapping-row">
                  <TableActionsSelect
                    options={primaryKeyColumnDroptionOptions}
                    placeHolder={`Select column for ${field.displayName}`}
                    value={field.selectedColumn || ''}
                    onChange={(event) => onContextFieldMapping(index, event.detail)}
                    label={field.displayName}
                  />
                </div>
              ))}
            </div>
          )}

          {allContextFieldsMapped && (
            <>
              <div className="actions-header forge-typography--subheading1">
                {I18n.t('enable_in', { scope })}
              </div>
              <ActionSelectorCheckbox
                dense={false}
                label={I18n.t('new_action_column', { scope })}
                isChecked={showTableActions}
                onClick={onNewActionClick}
              />
              {showTableActions &&
                (loading ? (
                  <ForgeSkeleton form-field></ForgeSkeleton>
                ) : (
                  <>
                    <MultiActionSelector
                      options={actionOptions}
                      label={I18n.t('title', { scope })}
                      value={newTableActions}
                      onChange={onMultipleActionSelect}
                      loading={false}
                    />
                    <ForgeDivider />
                  </>
                ))}
              <ActionSelectorCheckbox
                dense={false}
                label={I18n.t('existing.title', { scope })}
                isChecked={showExistingActionColumn}
                onClick={onExistingActionClick}
              />
              {showExistingActionColumn && (
                <>
                  <TableActionsSelect
                    options={tableColumnDroptionOptions}
                    placeHolder={I18n.t('existing.column_placeholder', { scope })}
                    value={selectedExistingColumn}
                    onChange={onColumnSelect}
                    label={I18n.t('existing.column_label', { scope })}
                  />
                  <TableActionsSelect
                    options={actionOptions}
                    placeHolder={I18n.t('existing.action_placeholder', { scope })}
                    label={I18n.t('existing.action_label', { scope })}
                    value={selectedActionForExistingColumn}
                    onChange={onActionSelect}
                  />
                </>
              )}
            </>
          )}
        </div>
      </div>
    </>
  );
};
